import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PartnerProfileHero from './widgets/PartnerProfileHero';
import PartnerStatCard from './widgets/PartnerStatCard';
import UpcomingEventsList from './widgets/UpcomingEventsList';
import { PortalPageHeader } from '../layout/PortalPageHeader';
import { usePartnerDashboardQuery } from '../../api/use-partner-dashboard-query';
import { PARTNER_PROFILE, PARTNER_STATS } from './partnerDashboardData';

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = usePartnerDashboardQuery();
  const upcomingEvents = data?.upcomingEvents ?? [];
  const upcomingStat = useMemo(() => {
    const stat = PARTNER_STATS.find((item) => item.id === 'upcoming-events-count');
    if (!stat) return null;
    return { ...stat, value: isLoading ? '' : upcomingEvents.length };
  }, [isLoading, upcomingEvents.length]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
      <PortalPageHeader title="Dashboard" />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 2.5,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/partner-events/new')}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '0.8125rem',
            px: 1.75,
            py: 0.875,
            whiteSpace: 'nowrap',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Add New Event
        </Button>
      </Box>

      <PartnerProfileHero name={PARTNER_PROFILE.name} description={PARTNER_PROFILE.description} />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
          sx={{ borderRadius: '12px' }}
        >
          {error?.message || 'Failed to load upcoming events'}
        </Alert>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch">
          {upcomingStat ? (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <PartnerStatCard {...upcomingStat} />
            </Grid>
          ) : null}
          <Grid
            item
            xs={12}
            sm={upcomingStat ? 6 : 12}
            md={upcomingStat ? 8 : 12}
            lg={upcomingStat ? 9 : 12}
          >
            <UpcomingEventsList events={upcomingEvents} />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default PartnerDashboard;
